import { Controller } from "@hotwired/stimulus";
import {
  GridApi,
  ColDef,
  IRowNode,
  ModuleRegistry,
  AllEnterpriseModule,
  LicenseManager,
} from "ag-grid-enterprise";
import type { SideBarDef, ToolPanelDef } from "ag-grid-enterprise";

ModuleRegistry.registerModules([AllEnterpriseModule]);

export const MIN_GRID_HEIGHT = 400;
// Small breathing room at the viewport bottom — footer is pushed below the fold
export const GRID_BOTTOM_MARGIN = 24;

export class AgGridBaseController extends Controller {
  static targets = [
    "grid",
    "actionsMenu",
    "selectedCount",
    "viewToggleBasic",
    "viewToggleAdvanced",
    "viewToggleMobile",
    "columnsToggle",
    "filterToggle",
    "exportToggle",
    "exportDropdown",
  ];

  static values = {
    licenseKey:        String,
    columnDefs:        Array,
    gridOptions:       Object,
    gridStateUrl:      String,
    advancedColumnDefs: Array,
    selectionActions:  Array,
  };

  declare readonly gridTarget: HTMLElement;
  declare readonly hasGridTarget: boolean;
  declare readonly actionsMenuTarget: HTMLElement;
  declare readonly hasActionsMenuTarget: boolean;
  declare readonly selectedCountTarget: HTMLElement;
  declare readonly hasSelectedCountTarget: boolean;
  declare readonly viewToggleBasicTarget: HTMLButtonElement;
  declare readonly viewToggleAdvancedTarget: HTMLButtonElement;
  declare readonly hasViewToggleBasicTarget: boolean;
  declare readonly hasViewToggleAdvancedTarget: boolean;
  declare readonly viewToggleMobileTarget: HTMLButtonElement;
  declare readonly hasViewToggleMobileTarget: boolean;
  declare readonly columnsToggleTarget: HTMLButtonElement;
  declare readonly hasColumnsToggleTarget: boolean;
  declare readonly filterToggleTarget: HTMLButtonElement;
  declare readonly hasFilterToggleTarget: boolean;
  declare readonly exportToggleTarget: HTMLButtonElement;
  declare readonly exportDropdownTarget: HTMLElement;
  declare readonly hasExportToggleTarget: boolean;
  declare readonly hasExportDropdownTarget: boolean;

  declare readonly licenseKeyValue: string;
  declare readonly columnDefsValue: ColDef[];
  declare readonly gridOptionsValue: Record<string, unknown>;
  declare readonly gridStateUrlValue: string;
  declare readonly advancedColumnDefsValue: ColDef[];
  declare readonly selectionActionsValue: Array<{
    label: string;
    url?: string;
    urlTemplate?: string;
    icon?: string;
    method?: string;
    rel?: string;
    confirm?: string;
    appendSelectedIds?: boolean;
    appendSelectedIdsParam?: string;
    minSelection?: number;
    maxSelection?: number;
    batchDelete?: boolean;
  }>;

  protected gridApi: GridApi | null = null;

  protected readonly rowSelectableCallback = (node: IRowNode): boolean =>
    (node.data as Record<string, unknown>)?.row_selectable !== false;
  protected isBasicMode = true;
  protected isRestoringState = false;
  protected pendingAutoSize = false;
  protected hasRowSelection = false;
  protected resizeHandler: (() => void) | null = null;
  protected outsideClickHandler: ((e: MouseEvent) => void) | null = null;
  protected gridBlurHandler: ((e: MouseEvent) => void) | null = null;

  connect() {
    if (this.licenseKeyValue) {
      LicenseManager.setLicenseKey(this.licenseKeyValue);
    }
    this.outsideClickHandler = (e: MouseEvent) => {
      const exportOpen = this.hasExportDropdownTarget && !this.exportDropdownTarget.hidden;
      if (exportOpen) {
        if (
          !this.exportToggleTarget.contains(e.target as Node) &&
          !this.exportDropdownTarget.contains(e.target as Node)
        ) {
          this.closeExportDropdown();
        }
      }
    };
    document.addEventListener("click", this.outsideClickHandler);
  }

  disconnect() {
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
      this.resizeHandler = null;
    }
    if (this.outsideClickHandler) {
      document.removeEventListener("click", this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
    if (this.gridBlurHandler) {
      document.removeEventListener("mousedown", this.gridBlurHandler);
      this.gridBlurHandler = null;
    }
    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
  }

  // ── Toolbar actions ────────────────────────────────────────────────────────

  toggleColumnsPanel() {
    if (!this.gridApi) return;
    this.closeExportDropdown();
    const isOpen = this.gridApi.getOpenedToolPanel() === "columns";
    if (isOpen) {
      this.gridApi.closeToolPanel();
      if (this.hasColumnsToggleTarget) this.columnsToggleTarget.setAttribute("aria-pressed", "false");
    } else {
      this.gridApi.openToolPanel("columns");
      if (this.hasColumnsToggleTarget) this.columnsToggleTarget.setAttribute("aria-pressed", "true");
      if (this.hasFilterToggleTarget) this.filterToggleTarget.setAttribute("aria-pressed", "false");
    }
  }

  toggleFilterPanel() {
    if (!this.gridApi) return;
    this.closeExportDropdown();
    const isOpen = this.gridApi.getOpenedToolPanel() === "filters-new";
    if (isOpen) {
      this.gridApi.closeToolPanel();
      if (this.hasFilterToggleTarget) this.filterToggleTarget.setAttribute("aria-pressed", "false");
    } else {
      this.gridApi.openToolPanel("filters-new");
      if (this.hasFilterToggleTarget) this.filterToggleTarget.setAttribute("aria-pressed", "true");
      if (this.hasColumnsToggleTarget) this.columnsToggleTarget.setAttribute("aria-pressed", "false");
    }
  }

  toggleViewMode() {
    if (this.isBasicMode) {
      this.switchToAdvanced();
    } else {
      this.switchToBasic();
    }
  }

  toggleExportDropdown() {
    if (!this.hasExportDropdownTarget) return;
    const isOpen = !this.exportDropdownTarget.hidden;
    // Close tool panels and reset their buttons so they don't appear active
    // while the export dropdown is open.
    this.gridApi?.closeToolPanel();
    if (this.hasColumnsToggleTarget) this.columnsToggleTarget.setAttribute("aria-pressed", "false");
    if (this.hasFilterToggleTarget) this.filterToggleTarget.setAttribute("aria-pressed", "false");
    if (isOpen) {
      this.closeExportDropdown();
    } else {
      this.exportDropdownTarget.hidden = false;
      if (this.hasExportToggleTarget) this.exportToggleTarget.setAttribute("aria-pressed", "true");
    }
  }

  // ── Abstract — concrete controllers must override ──────────────────────────

  switchToBasic(): void {
    throw new Error(`${this.constructor.name} must implement switchToBasic()`);
  }

  switchToAdvanced(): void {
    throw new Error(`${this.constructor.name} must implement switchToAdvanced()`);
  }

  // ── Protected helpers ──────────────────────────────────────────────────────

  protected setActiveToggle(mode: "basic" | "advanced") {
    if (!this.hasViewToggleBasicTarget || !this.hasViewToggleAdvancedTarget) return;
    const ACTIVE = "ag-grid-view-toggle__btn--active";
    if (mode === "basic") {
      this.viewToggleBasicTarget.classList.add(ACTIVE);
      this.viewToggleAdvancedTarget.classList.remove(ACTIVE);
    } else {
      this.viewToggleAdvancedTarget.classList.add(ACTIVE);
      this.viewToggleBasicTarget.classList.remove(ACTIVE);
    }
    if (this.hasViewToggleMobileTarget) {
      this.viewToggleMobileTarget.textContent = mode === "basic" ? "Basic" : "Advanced";
    }
  }

  protected closeExportDropdown() {
    if (!this.hasExportDropdownTarget) return;
    this.exportDropdownTarget.hidden = true;
    if (this.hasExportToggleTarget) this.exportToggleTarget.setAttribute("aria-pressed", "false");
  }

  protected initDynamicHeight() {
    this.resizeHandler = () => this.updateGridHeight();
    window.addEventListener("resize", this.resizeHandler);
    requestAnimationFrame(() => this.updateGridHeight());
  }

  protected updateGridHeight() {
    if (!this.hasGridTarget) return;
    const rect = this.gridTarget.getBoundingClientRect();
    const available = window.innerHeight - rect.top - GRID_BOTTOM_MARGIN;
    const height = Math.max(available, MIN_GRID_HEIGHT);
    this.gridTarget.style.height = `${height}px`;
  }

  protected syncPanelButtonStates() {
    if (!this.gridApi) return;
    const openPanel = this.gridApi.getOpenedToolPanel();
    if (this.hasColumnsToggleTarget) {
      this.columnsToggleTarget.setAttribute("aria-pressed", openPanel === "columns" ? "true" : "false");
    }
    if (this.hasFilterToggleTarget) {
      this.filterToggleTarget.setAttribute("aria-pressed", openPanel === "filters-new" ? "true" : "false");
    }
  }

  protected autoSizeNonFlexColumns() {
    if (!this.gridApi) return;
    requestAnimationFrame(() => {
      if (!this.gridApi) return;
      const visibleCols = this.gridApi.getAllDisplayedColumns() ?? [];
      if (visibleCols.length === 0) return;
      const colIds = visibleCols.map((col) => col.getColId());
      this.gridApi.autoSizeColumns(colIds);
      // Only flex the last column to fill spare space when all columns already fit
      // within the container — otherwise leave them at auto-sized widths so the
      // grid scrolls horizontally instead of squishing the last column.
      const containerWidth = this.gridTarget.clientWidth;
      const totalColWidth = visibleCols.reduce((sum, col) => sum + col.getActualWidth(), 0);
      if (totalColWidth < containerWidth) {
        const lastColId = colIds[colIds.length - 1];
        this.gridApi.applyColumnState({
          state: [{ colId: lastColId, flex: 1 }],
        });
      }
    });
  }

  protected renderActions(selectedRows: Array<Record<string, unknown>>): void {
    const actionsContainer = this.actionsMenuTarget.querySelector(
      ".ag-grid-component-actions-menu__actions"
    );
    if (!actionsContainer) return;

    actionsContainer.innerHTML = "";

    type RowAction = Record<string, string>;
    type StaticAction = typeof this.selectionActionsValue[number];

    const rowActions = selectedRows.map((row) =>
      Array.isArray(row.actions) ? (row.actions as RowAction[]) : []
    );

    const firstRowActions = rowActions[0] ?? [];
    const commonRowActions =
      rowActions.length === 1
        ? firstRowActions
        : firstRowActions.filter((action) =>
            rowActions.slice(1).every((otherActions) =>
              otherActions.some((a) => a.label === action.label)
            )
          );

    const selectedIds = selectedRows
      .map((row) => row.id)
      .filter((id) => id !== undefined && id !== null) as (string | number)[];

    const staticActions = this.selectionActionsValue;
    const hasActions = commonRowActions.length > 0 || staticActions.length > 0;
    if (!hasActions) return;

    const title = document.createElement("div");
    title.className = "ag-grid-component-actions-menu__title";
    title.textContent = "Actions";
    actionsContainer.appendChild(title);

    const buildLink = (action: RowAction | StaticAction, url: string) => {
      const link = document.createElement("a");
      link.className = "ag-grid-component-action-link";
      link.href = url;

      if (action.method && action.method !== "get") {
        link.dataset.method = action.method;
      }
      if (action.rel) {
        link.rel = action.rel;
      }
      if (action.confirm) {
        link.dataset.confirm = action.confirm;
      }

      if (action.icon) {
        const icon = document.createElement("i");
        icon.className = `fa fa-${action.icon}`;
        icon.setAttribute("aria-hidden", "true");
        link.appendChild(icon);
      }

      link.appendChild(document.createTextNode(` ${action.label}`));
      return link;
    };

    commonRowActions.forEach((action) => {
      actionsContainer.appendChild(buildLink(action, action.url || "#"));
    });

    const count = selectedRows.length;
    staticActions
      .filter((action) => {
        if (action.minSelection !== undefined && count < action.minSelection) return false;
        if (action.maxSelection !== undefined && count > action.maxSelection) return false;
        return true;
      })
      .forEach((action) => {
        if (action.batchDelete) {
          const btn = document.createElement("button");
          btn.className = "ag-grid-component-action-link";
          btn.type = "button";
          if (action.icon) {
            const icon = document.createElement("i");
            icon.className = `fa fa-${action.icon}`;
            icon.setAttribute("aria-hidden", "true");
            btn.appendChild(icon);
          }
          btn.appendChild(document.createTextNode(` ${action.label}`));
          btn.addEventListener("click", async () => {
            const msg = action.confirm ?? `Remove ${selectedIds.length} organization(s)? This cannot be undone.`;
            if (!window.confirm(msg)) return;
            const token = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? "";
            for (const id of selectedIds) {
              await fetch(`/organizations/${id}`, {
                method: "DELETE",
                headers: { "X-CSRF-Token": token, "X-Requested-With": "XMLHttpRequest" },
              });
            }
            window.location.reload();
          });
          actionsContainer.appendChild(btn);
          return;
        }

        let url = action.url || "#";
        if (action.urlTemplate) {
          url = action.urlTemplate.replace("{id}", String(selectedIds[0] ?? ""));
        } else if (action.appendSelectedIds && selectedIds.length > 0) {
          const param = action.appendSelectedIdsParam ?? "ids";
          const qs = selectedIds.map((id) => `${param}[]=${id}`).join("&");
          url += url.includes("?") ? `&${qs}` : `?${qs}`;
        }
        actionsContainer.appendChild(buildLink(action, url));
      });
  }

  protected clearActions(): void {
    const actionsContainer = this.actionsMenuTarget.querySelector(
      ".ag-grid-component-actions-menu__actions"
    );
    if (actionsContainer) {
      actionsContainer.innerHTML = "";
    }
  }

  protected buildSideBarConfig(hasAdvanced: boolean): false | SideBarDef {
    if (!hasAdvanced) return false;
    const toolPanels: ToolPanelDef[] = [
      {
        id: "columns",
        labelDefault: "Columns",
        labelKey: "columns",
        iconKey: "columns",
        toolPanel: "agColumnsToolPanel",
        toolPanelParams: { suppressColumnSelectAll: false },
      },
      {
        id: "filters-new",
        labelDefault: "Filters",
        labelKey: "filters",
        iconKey: "filter",
        toolPanel: "agNewFiltersToolPanel",
      },
    ];
    return { toolPanels, defaultToolPanel: "" };
  }

  protected basicColDefs(columnDefs: ColDef[]): ColDef[] {
    return columnDefs.map((c) => ({ ...c, suppressMovable: true }));
  }
}
